import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type MouseEvent as ReactMouseEvent, type ReactNode, type SetStateAction } from 'react';

/** Queue filter state that survives navigation. The dashboard swaps views, so
    MissionControlPage unmounts when a case opens and remounts fresh on return;
    persisting each filter per-user in sessionStorage keeps every queue toggle
    set when clicking between cases (Danny, 2026-10-22). */
function usePersistedState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [val, setVal] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(val));
    } catch {
      /* storage unavailable (private mode / quota) — stay in-memory */
    }
  }, [key, val]);
  return [val, setVal];
}
import {
  EngineError,
  getCaseAudit,
  getDocumentHtml,
  getDocumentPdfUrl,
  getMySources,
  listCaseDocuments,
  getOutreachCohorts,
  launchOutreach,
  type OutreachBlocked,
  type OutreachEligible,
  getSchedulingTask,
  getThreads,
  getCaseSummary,
  listAllCases,
  listCases,
  markCaseMessagesRead,
  searchCases,
  type CaseSearchHit,
  getMyPractice,
} from '../../api/engineDashboard';
import { deriveCaseInsights, mapLimit } from '../../lib/caseInsights';
import { deriveCaseStage } from '../../lib/caseStage';
import { StageMiniBar } from '../../components/dashboard/StageTracker';
import { setClerkArchived, followCase } from '../../api/engineDashboard';
import { ArchiveButton } from '../../components/ArchiveButton';
import { ManualCaseForm } from '../../components/dashboard/ManualCaseForm';
import {
  caseQueueLabel,
  caseRef,
  partyRoleLabel,
  caseStatusLabel,
  caseTitle,
  daysUntil,
  formatDateTime,
  OPEN_STATUSES,
} from '../../lib/labels';
import { formatDate } from '../../lib/format';
import { UnmatchedInbox } from '../../components/dashboard/UnmatchedInbox';
import { AgendaPanel } from '../../components/dashboard/AgendaPanel';
import type { Caps } from './DashboardApp';
import type { CaseListItem, SchedulingStatus } from '../../types/dashboard';
import type { CaseStatus } from '../../types/case';

// Triage signals mined per case (bounded scan over the open queue).
interface QueueInsight {
  waitingOnUs: number; // threads awaiting a reply from us
  schedulingStatus: SchedulingStatus | null;
  schedulingWaitingOn: string[]; // party names scheduling waits on
  booked: boolean;
  pendingDrafts: number;
}

type TriageKey = 'new_cases' | 'waiting_us' | 'waiting_party' | 'needs_scheduling' | 'unscheduled_trial' | 'drafts' | 'flagged';

const SCAN_LIMIT = 40; // most cases scanned for triage signals per load
const TRIAL_SOON_DAYS = 21;
const NEW_CASE_HOURS = 48;
// "Needs scheduling" applies to party-intake cases that expect a session —
// NOT the delinquency outreach ladder (which reaches a session only once a
// tenant engages). Give auto-scheduling an hour to run before flagging.
const SCHEDULABLE_SOURCES = ['pm_public_intake', 'court_public_intake', 'sms_intake', 'phone'];
const SCHEDULING_GRACE_HOURS = 1;

/** A case just arrived and likely needs first attention: freshly created
    and still open, or explicitly flagged for triage (Danny, 2026-09-04:
    highlight new cases on the dashboard). */
function isNewCase(c: CaseListItem): boolean {
  if (!OPEN_STATUSES.includes(c.status)) return false;
  if (c.tags?.includes('needs_triage')) return true;
  const ageMs = Date.now() - new Date(c.created_at).getTime();
  return ageMs >= 0 && ageMs <= NEW_CASE_HOURS * 3_600_000;
}

const PRIORITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

// ── Mediator triage view (Danny, 2026-10-11): replaces the tile grid with
// three working sections — new cases by partner, blocked/needs-attention,
// and communications — all driven off the case-list payload. ──────────────
const NEW_CASE_DAYS = 7;          // "New cases by partner": arrived this recently
const NOT_RESPONDING_DAYS = 10;   // "No reply": party silent at least this long
const TRIAL_ATTN_DAYS = 14;       // "Trial soon": trial within this many days
const TOP_N = 5;                  // show the top N rows, the rest behind a caret

const daysAgoOf = (iso?: string | null): number =>
  iso ? (Date.now() - new Date(iso).getTime()) / 86_400_000 : Infinity;

/** Partner grouping for "New cases by partner" (Danny, 2026-10-13): each
    court broken out — Jefferson County, Adams, then Other Courts — and each
    PM firm / direct source under its own name (Nomad, EZ Evict, …). `order`
    fixes the display order: courts first, then partners alphabetically. */
function partnerGroupOf(c: CaseListItem): { key: string; label: string; order: number } {
  if (c.org_source_type === 'court') {
    const n = (c.org_name ?? '').toLowerCase();
    if (n.includes('jeff')) return { key: 'court_jeff', label: 'Jefferson County', order: 0 };
    if (n.includes('adams')) return { key: 'court_adams', label: 'Adams County', order: 1 };
    return { key: 'court_other', label: 'Other Courts', order: 2 };
  }
  return { key: c.org_id ?? c.org_name ?? 'other', label: c.org_name ?? 'Levelheaded', order: 3 };
}

/** A "Missing Info" reason (info the case needs before it can proceed), or null. */
function missingInfoReason(c: CaseListItem): string | null {
  const meta = (c.metadata ?? {}) as Record<string, unknown>;
  if (c.tags?.includes('party2_unreachable')) return 'Other party unreachable';
  if (meta.returned_to_court) return 'Returned to court — no other-party contact';
  if (typeof meta.blocked_reason === 'string' && meta.blocked_reason) return 'Blocked';
  const principals = (c.parties ?? []).filter((p) => ['claimant', 'respondent'].includes(p.role_archetype));
  if (principals.some((p) => (p.metadata as Record<string, unknown> | undefined)?.placeholder)) return 'Other party info missing';
  const noContact = principals.find(
    (p) => !p.email && !p.phone && !(c.domain_pack_id === 'rent_delinquency' && p.role_archetype === 'claimant'),
  );
  if (noContact) return `Missing contact — ${noContact.name}`;
  return null;
}

function TrialTag({ trial }: { trial: string | null }) {
  if (!trial) return null;
  const d = daysUntil(trial);
  const soon = d !== null && d <= TRIAL_SOON_DAYS;
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${soon ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
      Trial {formatDate(trial)}{d !== null && d >= 0 ? ` · ${d}d` : ''}
    </span>
  );
}

function TriageCaseRow({ c, onOpen, right }: { c: CaseListItem; onOpen: () => void; right?: ReactNode }) {
  const { caseNo, name } = caseQueueLabel(c);
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-muted/60">
      <span className="min-w-0 truncate">
        {caseNo && <span className="font-mono text-xs text-gray-500">{caseNo} · </span>}
        <span className="font-medium text-gray-900">{name}</span>
      </span>
      {!c.assigned_mediator_id && (
        <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800" title="No mediator assigned yet — needs dispatch">
          Unassigned
        </span>
      )}
      <span className="ml-auto flex shrink-0 items-center gap-1.5">{right}</span>
    </button>
  );
}

function TriageSectionCard({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="card">
      <div className="flex items-center gap-2 border-b border-surface-border px-3 py-2">
        <span className="section-label">{title}</span>
        {count !== undefined && count > 0 && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">{count}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function TriageSubGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="bg-surface-muted/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      {children}
    </div>
  );
}

/** Top-N rows with the remainder behind a caret (Danny, 2026-10-13: show
    five everywhere, drop the rest behind a dropdown). */
function ExpandableRows({ rows, children, emptyText }: {
  rows: CaseListItem[];
  children: (c: CaseListItem) => ReactNode;
  emptyText?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (rows.length === 0) {
    return emptyText ? <p className="px-3 py-2 text-xs text-gray-400">{emptyText}</p> : null;
  }
  const shown = expanded ? rows : rows.slice(0, TOP_N);
  return (
    <>
      {shown.map(children)}
      {rows.length > TOP_N && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-[11px] font-medium text-brand hover:bg-surface-muted/60"
        >
          {expanded ? '▾ Show fewer' : `▸ Show all ${rows.length}`}
        </button>
      )}
    </>
  );
}

type BlockedTab = {
  key: string;
  label: string;
  rows: CaseListItem[];
  right: (c: CaseListItem) => ReactNode;
  empty: string;
};

/** Section 1 — Blocked · needs attention, tabbed (Danny, 2026-10-13):
    Trial ≤14d · No reply 10d+ · Missing info · COA generated · Not paid.
    Each tab shows the top five with the rest behind a caret; the tab bar
    opens on the first tab that actually has cases. */
function BlockedSection({ cases, onOpenCase }: {
  cases: CaseListItem[];
  onOpenCase: (id: string) => void;
}) {
  const open = cases.filter(
    (c) => OPEN_STATUSES.includes(c.status) && (c.metadata as Record<string, unknown>)?.test !== true && !c.clerk_archived,
  );
  const hasFutureSession = (c: CaseListItem) =>
    !!c.next_session_at && new Date(c.next_session_at).getTime() > Date.now();

  // Trial within 14 days and NOT already booked — the actionable set.
  const trial = open
    .filter((c) => {
      if (!c.trial_date) return false;
      const d = daysUntil(c.trial_date);
      return d >= 0 && d <= TRIAL_ATTN_DAYS && !hasFutureSession(c);
    })
    .sort((a, b) => (a.trial_date ?? '').localeCompare(b.trial_date ?? ''));
  const noReply = open
    .filter((c) => daysAgoOf(c.awaiting_party_since) >= NOT_RESPONDING_DAYS)
    .sort((a, b) => daysAgoOf(b.awaiting_party_since) - daysAgoOf(a.awaiting_party_since));
  const missing = open.filter((c) => missingInfoReason(c));
  const coa = cases.filter((c) => !c.clerk_archived && c.documents?.certificate);
  const notPaid = open.filter((c) => c.has_unpaid_invoice);
  // Everyone engaged, scheduling still stuck (Danny, 2026-10-31).
  const SCHED_BLOCK_LABELS: Record<string, string> = {
    no_overlap: 'no overlapping times',
    mediator_calendar: 'mediator calendar full',
    awaiting_confirmation: 'awaiting confirmations',
    timed_out: 'scheduling timed out',
  };
  const schedBlocked = open.filter((c) => c.scheduling_block && !hasFutureSession(c));

  const tabs: BlockedTab[] = [
    {
      key: 'trial',
      label: `Trial ≤${TRIAL_ATTN_DAYS}d`,
      rows: trial,
      right: (c) => <TrialTag trial={c.trial_date} />,
      empty: 'No unscheduled trials in the next two weeks.',
    },
    {
      key: 'noreply',
      label: `No reply ${NOT_RESPONDING_DAYS}d+`,
      rows: noReply,
      right: (c) => <span className="text-[11px] text-amber-700">silent {Math.floor(daysAgoOf(c.awaiting_party_since))}d</span>,
      empty: 'Everyone’s responsive.',
    },
    {
      key: 'missing',
      label: 'Missing info',
      rows: missing,
      right: (c) => <span className="text-[11px] text-amber-700">{missingInfoReason(c)}</span>,
      empty: 'Nothing missing.',
    },
    {
      key: 'coa',
      label: 'COA generated',
      rows: coa,
      right: (c) => <span className="text-[11px] text-gray-500">COA {c.documents?.certificate}</span>,
      empty: 'No certificates generated.',
    },
    {
      key: 'schedblock',
      label: 'Scheduling blocks',
      rows: schedBlocked,
      right: (c) => <span className="text-[11px] font-medium text-violet-700">{SCHED_BLOCK_LABELS[c.scheduling_block ?? ''] ?? c.scheduling_block}</span>,
      empty: 'No engaged-but-stuck scheduling.',
    },
    {
      key: 'unpaid',
      label: 'Not paid',
      rows: notPaid,
      right: () => <span className="text-[11px] font-medium text-red-700">unpaid invoice</span>,
      empty: 'All invoices paid.',
    },
  ];
  const [active, setActive] = useState<string>(() => tabs.find((t) => t.rows.length > 0)?.key ?? tabs[0].key);
  const current = tabs.find((t) => t.key === active) ?? tabs[0];
  const unique = new Set([...trial, ...noReply, ...missing, ...coa, ...notPaid, ...schedBlocked].map((c) => c.id));

  return (
    <TriageSectionCard title="Blocked · needs attention" count={unique.size}>
      <div className="flex flex-wrap gap-1 border-b border-surface-border px-2 py-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActive(t.key)}
            className={[
              'rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
              t.key === active ? 'bg-brand text-white' : 'bg-surface-muted text-gray-600 hover:bg-surface-muted/70',
            ].join(' ')}
          >
            {t.label}
            {t.rows.length > 0 && (
              <span className={`ml-1 ${t.key === active ? 'text-white/80' : 'text-gray-400'}`}>{t.rows.length}</span>
            )}
          </button>
        ))}
      </div>
      <ExpandableRows rows={current.rows} emptyText={current.empty}>
        {(c) => <TriageCaseRow key={c.id} c={c} onOpen={() => onOpenCase(c.id)} right={current.right(c)} />}
      </ExpandableRows>
    </TriageSectionCard>
  );
}

/** One partner's recent cases: sub-header + top-five with a caret for the
    rest (each partner expands independently). */
function PartnerGroup({ label, rows, onOpenCase }: {
  label: string;
  rows: CaseListItem[];
  onOpenCase: (id: string) => void;
}) {
  const sorted = [...rows].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  return (
    <TriageSubGroup label={`${label} · ${rows.length}`}>
      <ExpandableRows rows={sorted}>
        {(c) => (
          <TriageCaseRow
            key={c.id}
            c={c}
            onOpen={() => onOpenCase(c.id)}
            right={
              <>
                <span className="text-[11px] text-gray-400">{formatDate(c.created_at)}</span>
                <TrialTag trial={c.trial_date} />
              </>
            }
          />
        )}
      </ExpandableRows>
    </TriageSubGroup>
  );
}

/** Section 2 — New cases by partner (arrived in the last NEW_CASE_DAYS;
    rows age out of this section after a week). Courts first, then partners. */
function NewCasesSection({ cases, onOpenCase }: {
  cases: CaseListItem[];
  onOpenCase: (id: string) => void;
}) {
  const fresh = cases.filter(
    (c) =>
      OPEN_STATUSES.includes(c.status) &&
      (c.metadata as Record<string, unknown>)?.test !== true &&
      !c.clerk_archived &&
      daysAgoOf(c.created_at) <= NEW_CASE_DAYS,
  );
  const groups = new Map<string, { label: string; order: number; rows: CaseListItem[] }>();
  for (const c of fresh) {
    const g = partnerGroupOf(c);
    if (!groups.has(g.key)) groups.set(g.key, { label: g.label, order: g.order, rows: [] });
    groups.get(g.key)!.rows.push(c);
  }
  const ordered = [...groups.entries()].sort(
    (a, b) => a[1].order - b[1].order || a[1].label.localeCompare(b[1].label),
  );

  return (
    <TriageSectionCard title="New cases by partner" count={fresh.length}>
      {fresh.length === 0 ? (
        <p className="px-3 py-3 text-sm text-gray-500">No new intakes in the last {NEW_CASE_DAYS} days.</p>
      ) : (
        <div className="divide-y divide-surface-border">
          {ordered.map(([key, g]) => (
            <PartnerGroup key={key} label={g.label} rows={g.rows} onOpenCase={onOpenCase} />
          ))}
        </div>
      )}
    </TriageSectionCard>
  );
}

/** Right rail — "Hi {name}": the mediator's open-case count and unread
    (awaiting-reply) count, then the cases waiting on a reply, top five with
    a caret. Clicking a row jumps to that case's Messages panel. */
function CommsRail({ cases, selfId, selfName, onOpenMessages, onMarkRead, summary, awaitingCases, onShowUnassigned }: {
  cases: CaseListItem[];
  selfId: string;
  selfName: string;
  onOpenMessages: (id: string) => void;
  onMarkRead: (id: string) => void;
  summary: { open: number; awaiting_reply: number; unassigned_awaiting?: number } | null;
  awaitingCases: CaseListItem[] | null;
  onShowUnassigned?: () => void;
}) {
  const open = cases.filter(
    (c) => OPEN_STATUSES.includes(c.status) && (c.metadata as Record<string, unknown>)?.test !== true && !c.clerk_archived,
  );
  const mine = open.filter((c) => c.assigned_mediator_id === selfId);
  // The unread list is the viewer's ACTUAL awaiting-reply cases, fetched by id
  // (so it shows all of them, matching the count) — falling back to the loaded
  // page only until that lands.
  const unread = (awaitingCases ?? mine.filter((c) => c.awaiting_reply))
    .slice()
    .sort((a, b) => (b.last_comm_at ?? '').localeCompare(a.last_comm_at ?? ''));
  // Tile counts come from the server summary (the viewer's full caseload,
  // independent of the source filter + 100-row page); the loaded-page figures
  // are only a placeholder until it lands.
  const openCount = summary?.open ?? mine.length;
  const awaitingCount = summary?.awaiting_reply ?? unread.length;
  const firstName = (selfName || '').trim().split(/\s+/)[0] || 'there';

  return (
    <aside className="space-y-3 lg:sticky lg:top-16 lg:self-start">
      <section className="card p-4">
        <div className="text-base font-semibold text-gray-900">Hi {firstName}</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-surface-muted/60 px-3 py-2">
            <div className="text-2xl font-semibold tabular-nums text-gray-900">{openCount}</div>
            <div className="text-[11px] font-medium text-gray-500">Your open cases</div>
          </div>
          <div className="rounded-lg bg-surface-muted/60 px-3 py-2">
            <div className="flex items-center gap-1.5 text-2xl font-semibold tabular-nums text-gray-900">
              {awaitingCount}
              {awaitingCount > 0 && <span aria-hidden className="h-2 w-2 rounded-full bg-amber-500" />}
            </div>
            <div className="text-[11px] font-medium text-gray-500">Awaiting your reply</div>
          </div>
        </div>
        {/* Ownership-gap safety net (2026-10-26): unassigned cases' awaiting-reply
            signal appears in nobody's personal queue — surface it to admins here. */}
        {(summary?.unassigned_awaiting ?? 0) > 0 && (
          <button
            type="button"
            onClick={onShowUnassigned}
            className="mt-2 flex w-full items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left text-xs font-medium text-amber-900 hover:bg-amber-100"
            title="Open the unassigned-cases filter"
          >
            <span aria-hidden>⚠️</span>
            {summary!.unassigned_awaiting} unassigned case{summary!.unassigned_awaiting === 1 ? '' : 's'} awaiting a reply — nobody&apos;s queue has {summary!.unassigned_awaiting === 1 ? 'it' : 'them'}
          </button>
        )}
      </section>

      <TriageSectionCard title="Awaiting your reply" count={awaitingCount}>
        {unread.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500">
            {awaitingCount > 0 ? 'Loading…' : 'You’re all caught up.'}
          </p>
        ) : (
          <ExpandableRows rows={unread}>
            {(c) => (
              <TriageCaseRow
                key={c.id}
                c={c}
                onOpen={() => onOpenMessages(c.id)}
                right={
                  <span className="flex items-center gap-2">
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                      {c.last_comm_at ? formatDate(c.last_comm_at) : 'new'}
                    </span>
                    {/* Dismiss WITHOUT replying — a newer party message
                        re-raises it (Danny, 2026-10-22). */}
                    <button
                      type="button"
                      className="rounded px-1.5 py-0.5 text-[11px] font-medium text-gray-500 hover:bg-surface-muted hover:text-gray-700"
                      title="Take this off the unread list without replying. It comes back if the party writes again."
                      onClick={(e) => { e.stopPropagation(); onMarkRead(c.id); }}
                    >
                      Read
                    </button>
                  </span>
                }
              />
            )}
          </ExpandableRows>
        )}
      </TriageSectionCard>
    </aside>
  );
}

/** Mediator home: Blocked + New-cases in the main column, the personalized
    communications rail on the right (Danny, 2026-10-13). */
function MediatorTriage({ cases, selfId, selfName, onOpenCase, onOpenMessages, onMarkRead, summary, awaitingCases, onShowUnassigned }: {
  cases: CaseListItem[];
  selfId: string;
  selfName: string;
  onOpenCase: (id: string) => void;
  onOpenMessages: (id: string) => void;
  onMarkRead: (id: string) => void;
  summary: { open: number; awaiting_reply: number; unassigned_awaiting?: number } | null;
  awaitingCases: CaseListItem[] | null;
  onShowUnassigned?: () => void;
}) {
  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-3">
        <BlockedSection cases={cases} onOpenCase={onOpenCase} />
        <NewCasesSection cases={cases} onOpenCase={onOpenCase} />
      </div>
      <CommsRail cases={cases} selfId={selfId} selfName={selfName} onOpenMessages={onOpenMessages} onMarkRead={onMarkRead} summary={summary} awaitingCases={awaitingCases} onShowUnassigned={onShowUnassigned} />
    </div>
  );
}

interface Props {
  token: string;
  caps: Caps;
  /** Signed-in mediator — drives the "Hi {name}" rail + "yours" counts. */
  selfId: string;
  selfName: string;
  onOpenCase: (caseId: string) => void;
  onUnauthorized: () => void;
}

export function MissionControlPage({ token, caps, selfId, selfName, onOpenCase, onUnauthorized }: Props) {
  // Practice onboarding (Mediator OS Phase 2): tenant members see a
  // getting-started checklist until their practice is fully set up.
  const [practice, setPractice] = useState<Awaited<ReturnType<typeof getMyPractice>>['practice']>(null);
  useEffect(() => {
    getMyPractice(token).then((res) => setPractice(res.practice)).catch(() => {});
  }, [token]);
  // Unmatched-inbox count, surfaced as a tile up top so waiting messages are
  // visible at a glance (Danny, 2026-10-02) — clicking jumps to the inbox.
  const unmatchedRef = useRef<HTMLDivElement>(null);
  const [cases, setCases] = useState<CaseListItem[] | null>(null);
  // Authoritative "Hi {name}" tile counts — the viewer's own caseload,
  // server-computed and independent of the queue filters / 100-row page, so
  // they stop swinging with the source dropdown (Danny, 2026-10-23).
  const [summary, setSummary] = useState<{ open: number; awaiting_reply: number; unassigned_awaiting?: number } | null>(null);
  // The viewer's actual awaiting-reply cases (full rows), fetched by id so the
  // rail shows ALL of them — not just the ones on the filtered queue page.
  const [awaitingCases, setAwaitingCases] = useState<CaseListItem[] | null>(null);
  const refreshSummary = useCallback(() => {
    if (caps.isClerk) return;
    getCaseSummary(token)
      .then((s) => {
        setSummary({ open: s.open, awaiting_reply: s.awaiting_reply });
        if (s.awaiting_case_ids.length) {
          listCases(token, { ids: s.awaiting_case_ids, limit: 50 })
            .then((res) => setAwaitingCases(res.data))
            .catch(() => setAwaitingCases([]));
        } else {
          setAwaitingCases([]);
        }
      })
      .catch(() => {});
  }, [token, caps.isClerk]);
  useEffect(refreshSummary, [refreshSummary]);
  const [courtLogo, setCourtLogo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [insights, setInsights] = useState<Record<string, QueueInsight>>({});
  const [, setScanning] = useState(false);

  const [statusFilter, setStatusFilter] = usePersistedState<'' | 'open' | CaseStatus>(`lh-q-${selfId}-status`, 'open');
  const [priorityFilter, setPriorityFilter] = usePersistedState(`lh-q-${selfId}-priority`, '');
  // Server-side narrowing (the queue loads at most 100 rows — with many
  // sources the interesting ones can fall off the page, Danny 2026-08-24).
  const [sourceFilter, setSourceFilter] = usePersistedState(`lh-q-${selfId}-source`, '');
  const [migratedOnly, setMigratedOnly] = usePersistedState(`lh-q-${selfId}-migrated`, false);
  // Admin mediators default to THEIR OWN cases (any status); they can toggle
  // "My cases" off to see every case (Danny, 2026-10-22, reverting the
  // 2026-10-19 all-partners-unscheduled default). The default applies until
  // the user changes it, then their choice persists across navigation.
  const [mineOnly, setMineOnly] = usePersistedState(`lh-q-${selfId}-mine`, caps.isAdminMediator);
  // Unassigned-only: ownerless cases needing a mediator (mutually exclusive
  // with "My cases" — Danny, 2026-10-23).
  const [unassignedOnly, setUnassignedOnly] = usePersistedState(`lh-q-${selfId}-unassigned`, false);
  const [unscheduledOnly, setUnscheduledOnly] = usePersistedState(`lh-q-${selfId}-unscheduled`, false);
  const [followedOnly, setFollowedOnly] = usePersistedState(`lh-q-${selfId}-followed`, false);
  const [showNewCase, setShowNewCase] = useState(false);
  // In-page document viewer (clerk ask 2026-08-26: an "eye" that pops the
  // document up view-only instead of downloading).
  const [docViewer, setDocViewer] = useState<{ title: string; url: string; filename: string } | null>(null);
  // Inbox tile (Danny, 2026-08-28): unread internal-message count; active
  // = the queue sorts by most recent internal message.
  const [inboxSort, setInboxSort] = usePersistedState(`lh-q-${selfId}-inbox`, false);
  const [sources, setSources] = useState<Array<{ id: string; name: string }>>([]);
  const [search, setSearch] = usePersistedState(`lh-q-${selfId}-search`, '');
  /** Server search hits keyed by case id; null = search inactive. */
  const [searchHits, setSearchHits] = useState<Map<string, CaseSearchHit> | null>(null);
  const [sort, setSort] = usePersistedState<'updated_at' | 'created_at' | 'priority' | 'trial_date'>(
    `lh-q-${selfId}-sort`,
    'updated_at',
  );
  const [triageFilter] = useState<TriageKey | null>(null);

  // Clerk archive — server-side (metadata.clerk_archived), shared across
  // every clerk/judge account for the court. Seeded from the case list;
  // toggles update optimistically and revert on API failure.
  const [archived, setArchived] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = usePersistedState(`lh-q-${selfId}-showArchived`, false);
  // Search hits on cases beyond the loaded page, hydrated on demand.
  const [searchExtra, setSearchExtra] = useState<CaseListItem[]>([]);
  const toggleFollow = useCallback(
    (caseId: string, next: boolean) => {
      // Optimistic: flip the row's followed flag, revert on failure.
      setCases((prev) => prev?.map((c) => (c.id === caseId ? { ...c, followed: next } : c)) ?? prev);
      followCase(token, caseId, next).catch(() => {
        setCases((prev) => prev?.map((c) => (c.id === caseId ? { ...c, followed: !next } : c)) ?? prev);
      });
    },
    [token],
  );

  const toggleArchive = useCallback(
    (caseId: string, next: boolean) => {
      setArchived((prev) => {
        const ids = new Set(prev);
        if (next) ids.add(caseId);
        else ids.delete(caseId);
        return ids;
      });
      setClerkArchived(token, caseId, next).catch(() => {
        setArchived((prev) => {
          const ids = new Set(prev);
          if (next) ids.delete(caseId);
          else ids.add(caseId);
          return ids;
        });
      });
    },
    [token],
  );

  // "Read": take a case off the awaiting-reply list without replying. The
  // engine stamps the thread so a newer inbound re-raises it (migration 040).
  const markRead = useCallback(
    (caseId: string) => {
      // Optimistic: drop it from the unread list now, revert on failure.
      setCases((prev) => prev?.map((c) => (c.id === caseId ? { ...c, awaiting_reply: false } : c)) ?? prev);
      setSummary((prev) => (prev ? { ...prev, awaiting_reply: Math.max(0, prev.awaiting_reply - 1) } : prev));
      markCaseMessagesRead(token, caseId)
        .then(refreshSummary)
        .catch(() => {
          setCases((prev) => prev?.map((c) => (c.id === caseId ? { ...c, awaiting_reply: true } : c)) ?? prev);
          refreshSummary();
        });
    },
    [token, refreshSummary],
  );

  const fail = useCallback(
    (err: unknown) => {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load cases');
    },
    [onUnauthorized],
  );

  // Sources: the filter dropdown for everyone; court logo for the clerk
  // list header (spec 2.9 — org intake config).
  useEffect(() => {
    getMySources(token)
      .then((res) => {
        setSources(res.sources.map((o) => ({ id: o.id, name: o.name })));
        if (!caps.isClerk) return;
        const court = res.sources.find((o) => o.source_type === 'court' && o.logo_url);
        if (court?.logo_url) setCourtLogo(court.logo_url);
      })
      .catch(() => {});
  }, [caps.isClerk, token]);

  // Load the queue, then mine triage signals for open cases.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    listAllCases(token, {
      ...(sourceFilter ? { org_id: sourceFilter } : {}),
      ...(migratedOnly ? { tag: 'v1_migration' } : {}),
      ...(unassignedOnly ? { assigned: 'none' as const } : mineOnly ? { assigned: 'me' as const } : {}),
      ...(unscheduledOnly ? { unscheduled: true } : {}),
      ...(followedOnly ? { followed: true } : {}),
      // Always fetch archived rows: visibility is filtered client-side,
      // and the inbox tile must COUNT an archived case's unread internal
      // message (Brad's tile said 2 when the truth was 3, 2026-09-17).
      include_archived: true,
    })
      .then(async (res) => {
        if (cancelled) return;
        setCases(res.data);
        // Belt and suspenders: read the flag OR the metadata directly, so
        // an older engine can never flood the queue with archived rows.
        setArchived(
          new Set(
            res.data
              .filter((c) => c.clerk_archived || ((c.metadata ?? {}) as Record<string, unknown>).clerk_archived === true)
              .map((c) => c.id),
          ),
        );
        if (caps.isClerk) return; // clerk view has no triage scan

        const scanTargets = res.data
          .filter((c) => OPEN_STATUSES.includes(c.status))
          .slice(0, SCAN_LIMIT);
        setScanning(true);
        await mapLimit(scanTargets, 6, async (c) => {
          try {
            const [threadsRes, auditRes] = await Promise.all([
              getThreads(token, c.id).catch(() => ({ threads: [] })),
              getCaseAudit(token, c.id).catch(() => ({ data: [] })),
            ]);
            const derived = deriveCaseInsights(auditRes.data);
            let schedulingStatus: SchedulingStatus | null = null;
            let waitingOn: string[] = [];
            let booked = Boolean(derived.bookedSessionId);
            if (derived.schedulingTaskId) {
              try {
                const task = await getSchedulingTask(token, derived.schedulingTaskId);
                schedulingStatus = task.status;
                waitingOn = task.waiting_on;
                booked = booked || Boolean(task.booked_slot);
              } catch {
                /* task gone or not visible — audit signals still stand */
              }
            }
            if (cancelled) return;
            setInsights((prev) => ({
              ...prev,
              [c.id]: {
                waitingOnUs: threadsRes.threads.filter((t) => t.awaiting === 'us').length,
                schedulingStatus,
                schedulingWaitingOn: waitingOn,
                booked,
                pendingDrafts: derived.pendingDrafts.length,
              },
            }));
          } catch {
            /* per-case scan failures never break the queue */
          }
        });
        if (!cancelled) setScanning(false);
      })
      .catch((err: unknown) => {
        if (!cancelled) fail(err);
      });
    return () => {
      cancelled = true;
    };
  }, [token, caps.isClerk, fail, sourceFilter, migratedOnly, mineOnly, unassignedOnly, unscheduledOnly, followedOnly, showArchived]);

  const matchesTriage = useCallback(
    (c: CaseListItem, key: TriageKey): boolean => {
      const ins = insights[c.id];
      switch (key) {
        case 'new_cases':
          return isNewCase(c);
        case 'waiting_us':
          return (ins?.waitingOnUs ?? 0) > 0;
        case 'waiting_party':
          return (ins?.schedulingWaitingOn.length ?? 0) > 0;
        case 'needs_scheduling': {
          // An intake that should have a session scheduled but doesn't —
          // no task started, nothing booked. Only count scanned cases (the
          // no-task signal comes from the scan) with a reachable party, past
          // the auto-schedule grace window.
          if (!ins) return false;
          if (!OPEN_STATUSES.includes(c.status)) return false;
          if (c.metadata?.test === true) return false; // never flag test data
          if (ins.schedulingStatus || ins.booked) return false;
          const src = typeof c.metadata?.source === 'string' ? c.metadata.source : '';
          if (!SCHEDULABLE_SOURCES.includes(src)) return false;
          if (!(c.parties ?? []).some((p) => p.email || p.phone)) return false;
          const ageMs = Date.now() - new Date(c.created_at).getTime();
          return ageMs >= SCHEDULING_GRACE_HOURS * 3_600_000;
        }
        case 'drafts':
          return (ins?.pendingDrafts ?? 0) > 0;
        case 'unscheduled_trial':
          return (
            OPEN_STATUSES.includes(c.status) &&
            c.trial_date !== null &&
            daysUntil(c.trial_date) <= TRIAL_SOON_DAYS &&
            !(ins?.booked ?? false)
          );
        case 'flagged':
          return OPEN_STATUSES.includes(c.status) && caseFlags(c, ins).length > 0;
      }
    },
    [insights],
  );

  // Debounced server search: party names, message content, case numbers.
  // Hits on cases outside the loaded page are hydrated separately so a
  // search reaches EVERY stage and the full history, not just the rows
  // already on screen.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSearchHits(null);
      setSearchExtra([]);
      return;
    }
    const timer = setTimeout(() => {
      searchCases(token, q)
        .then(async (res) => {
          setSearchHits(new Map(res.results.map((r) => [r.case_id, r])));
          const loaded = new Set((cases ?? []).map((c) => c.id));
          const missing = res.results.map((r) => r.case_id).filter((id) => !loaded.has(id));
          if (missing.length) {
            try {
              const extra = await listCases(token, {
                ids: missing,
                limit: 50,
                // Search COMPOSES with the queue filters (Danny, 2026-10-31):
                // rows pulled in purely for a search hit obey the same
                // source/assignment toggles as the browse list.
                ...(sourceFilter ? { org_id: sourceFilter } : {}),
                ...(migratedOnly ? { tag: 'v1_migration' } : {}),
                ...(unassignedOnly ? { assigned: 'none' as const } : mineOnly ? { assigned: 'me' as const } : {}),
                ...(unscheduledOnly ? { unscheduled: true } : {}),
                ...(followedOnly ? { followed: true } : {}),
                include_archived: true,
              });
              setSearchExtra(extra.data);
            } catch {
              setSearchExtra([]);
            }
          } else {
            setSearchExtra([]);
          }
        })
        .catch((err: unknown) => {
          if (err instanceof EngineError && err.status === 401) return onUnauthorized();
          setSearchHits(new Map()); // search failed — show no matches rather than lying
          setSearchExtra([]);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, token, cases, onUnauthorized, sourceFilter, migratedOnly, unassignedOnly, mineOnly, unscheduledOnly, followedOnly]);

  const visible = useMemo(() => {
    if (!cases) return [];
    const searching = searchHits !== null;
    let rows = searching ? [...cases, ...searchExtra.filter((e) => !cases.some((c) => c.id === e.id))] : cases;
    // Search COMPOSES with the filters (Danny, 2026-10-31: searching a
    // phone with Nomad selected shows ONLY Nomad matches). Two deliberate
    // carve-outs under an active search: archived rows stay reachable
    // (search is how you find them), and the DEFAULT "All open" stage
    // widens to every stage so closed cases stay findable by search (the
    // ROM-close promise) — an explicitly chosen stage still constrains.
    // Inbox mode (the unread tile): a case with an unread internal
    // message is ALWAYS visible — closed, archived, filtered or not.
    // The tile counts them, so the list must be able to show them
    // (judge Brad Varmo's phantom "2 unread", 2026-09-17).
    const pinned = (c: (typeof rows)[number]) => inboxSort && c.internal_unread;
    if (!searching) {
      rows = rows.filter((c) => pinned(c) || showArchived || !archived.has(c.id));
    }
    if (statusFilter === 'open') {
      if (!searching) rows = rows.filter((c) => pinned(c) || OPEN_STATUSES.includes(c.status));
    }
    // "Resolved" includes cases that resolved and then closed (full-
    // resolution manual closes, ROM auto-closes) — resolution is the
    // outcome, CLOSED is just the filing state (Danny, 2026-08-28).
    else if (statusFilter === 'RESOLVED')
      rows = rows.filter(
        (c) => c.status === 'RESOLVED' || (c.status === 'CLOSED' && (c.metadata ?? {}).mediation_outcome === 'resolved'),
      );
    else if (statusFilter === 'CLOSED')
      rows = rows.filter((c) => c.status === 'CLOSED' && (c.metadata ?? {}).mediation_outcome !== 'resolved');
    else if (statusFilter) rows = rows.filter((c) => pinned(c) || c.status === statusFilter);
    if (priorityFilter) rows = rows.filter((c) => pinned(c) || c.priority === priorityFilter);
    if (triageFilter) rows = rows.filter((c) => pinned(c) || matchesTriage(c, triageFilter));
    if (searchHits !== null) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          searchHits.has(c.id) ||
          caseTitle(c.metadata, c.claims).toLowerCase().includes(q) ||
          caseRef(c.id).toLowerCase().includes(q) ||
          (() => {
            const l = caseQueueLabel(c);
            return (
              l.name.toLowerCase().includes(q) ||
              (l.partner ?? '').toLowerCase().includes(q) ||
              (l.caseNo ?? '').toLowerCase().includes(q)
            );
          })(),
      );
    }

    const sorted = [...rows];
    if (inboxSort) {
      sorted.sort((a, b) => {
        // Unread cases first — they are what the tile promised.
        const au = a.internal_unread ? 0 : 1;
        const bu = b.internal_unread ? 0 : 1;
        if (au !== bu) return au - bu;
        const al = a.internal_last_at ?? '';
        const bl = b.internal_last_at ?? '';
        if (al !== bl) return bl.localeCompare(al); // newest message first; never-messaged sink
        return b.updated_at.localeCompare(a.updated_at);
      });
      return sorted;
    }
    sorted.sort((a, b) => {
      switch (sort) {
        case 'priority':
          return (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
        case 'trial_date': {
          if (!a.trial_date && !b.trial_date) return 0;
          if (!a.trial_date) return 1; // no trial date sinks
          if (!b.trial_date) return -1;
          return a.trial_date.localeCompare(b.trial_date);
        }
        case 'created_at':
          return b.created_at.localeCompare(a.created_at);
        default:
          return b.updated_at.localeCompare(a.updated_at);
      }
    });
    return sorted;
  }, [cases, caps.isClerk, showArchived, archived, statusFilter, priorityFilter, triageFilter, sort, inboxSort, matchesTriage, searchHits, search, searchExtra]);

  if (error) {
    return (
      <div className="card p-6 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button type="button" className="btn-secondary mt-3" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  if (!cases) {
    return <div className="p-8 text-center text-sm text-gray-500">Loading your cases…</div>;
  }

  const unreadCount = (cases ?? []).filter((c) => c.internal_unread).length;
  const inboxTile = (
    <InboxTile
      count={unreadCount}
      active={inboxSort}
      onClick={() => setInboxSort((v) => !v)}
    />
  );

  return (
    <div className="space-y-4">
      {/* Mediator onboarding checklist (Connect Zoom, availability, intake
          link…) is mediator setup — never show it to clerks (Danny/Mo,
          2026-10-21). */}
      {!caps.isClerk && <PracticeOnboardingCard practice={practice} />}
      {caps.isClerk && <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">{inboxTile}</section>}
      {!caps.isClerk && (
        <MediatorTriage
          cases={cases}
          selfId={selfId}
          selfName={selfName}
          onOpenCase={onOpenCase}
          onMarkRead={markRead}
          summary={summary}
          awaitingCases={awaitingCases}
          onShowUnassigned={() => { setMineOnly(false); setUnassignedOnly(true); }}
          onOpenMessages={(id) => {
            try { sessionStorage.setItem('lh-scroll-messages', id); } catch { /* ignore */ }
            onOpenCase(id);
          }}
        />
      )}

      {!caps.isClerk && (
        <AgendaPanel
          token={token}
          isAdmin={caps.role === 'admin'}
          onOpenCase={onOpenCase}
          onUnauthorized={onUnauthorized}
        />
      )}

      {caps.canMutate && <OutreachLaunchPanel token={token} />}

      {docViewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            URL.revokeObjectURL(docViewer.url);
            setDocViewer(null);
          }}
        >
          <div className="flex h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-surface-border px-4 py-2">
              <div className="text-sm font-semibold">{docViewer.title}</div>
              <div className="flex items-center gap-3">
                <a
                  href={docViewer.url}
                  download={docViewer.filename}
                  className="text-sm text-brand hover:underline"
                >
                  ⬇ Download PDF
                </a>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-700"
                  onClick={() => {
                    URL.revokeObjectURL(docViewer.url);
                    setDocViewer(null);
                  }}
                >
                  ✕ close
                </button>
              </div>
            </div>
            <iframe title={docViewer.title} className="h-full w-full flex-1" src={docViewer.url} />
          </div>
        </div>
      )}
      {showNewCase && !caps.isClerk && (
        <ManualCaseForm
          token={token}
          sources={sources}
          onClose={() => setShowNewCase(false)}
          onCreated={(caseId) => {
            setShowNewCase(false);
            onOpenCase(caseId);
          }}
        />
      )}
      <section className="card">
        <div className="flex flex-wrap items-center gap-2 border-b border-surface-border p-3">
          <div className="flex items-center gap-3">
            {courtLogo && <img src={courtLogo} alt="Court" className="h-10 w-auto" />}
            <div className="section-label">Case queue</div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
            <input
              type="search"
              className="w-44 rounded-md border border-surface-border px-2 py-1"
              placeholder="Search names, messages…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {!caps.isClerk && (
              <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setShowNewCase((v) => !v)}>
                + New case
              </button>
            )}
            {sources.length > 1 && (
              <select
                className="rounded-md border border-surface-border px-2 py-1"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                title="Show only this source's cases"
              >
                <option value="">All sources</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
            {!caps.isClerk && (
              <label className="flex items-center gap-1 text-gray-600" title="Only cases assigned to you">
                <input
                  type="checkbox"
                  checked={mineOnly}
                  onChange={(e) => {
                    setMineOnly(e.target.checked);
                    if (e.target.checked) setUnassignedOnly(false);
                  }}
                />
                My cases
              </label>
            )}
            {!caps.isClerk && (
              <label className="flex items-center gap-1 text-amber-700" title="Only cases with no mediator assigned yet — needing dispatch">
                <input
                  type="checkbox"
                  checked={unassignedOnly}
                  onChange={(e) => {
                    setUnassignedOnly(e.target.checked);
                    if (e.target.checked) setMineOnly(false);
                  }}
                />
                Unassigned
              </label>
            )}
            {!caps.isClerk && (
              <label className="flex items-center gap-1 text-gray-600" title="Only cases that are not yet scheduled (pre-mediation)">
                <input type="checkbox" checked={unscheduledOnly} onChange={(e) => setUnscheduledOnly(e.target.checked)} />
                Unscheduled only
              </label>
            )}
            {!caps.isClerk && (
              <label className="flex items-center gap-1 text-gray-600" title="Only cases you follow">
                <input type="checkbox" checked={followedOnly} onChange={(e) => setFollowedOnly(e.target.checked)} />
                ★ Following
              </label>
            )}
            {!caps.isClerk && (
              <label className="flex items-center gap-1 text-gray-600" title="Only cases migrated from the previous platform">
                <input type="checkbox" checked={migratedOnly} onChange={(e) => setMigratedOnly(e.target.checked)} />
                Migrated
              </label>
            )}
            <select
              className="rounded-md border border-surface-border px-2 py-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="open">All open</option>
              <option value="">Everything</option>
              {(
                ['INTAKE', 'ASSESSING', 'GATHERING', 'EVALUATING', 'NEGOTIATING', 'ESCALATING', 'MEDIATING', 'STALLED', 'RESOLVED', 'CLOSED'] as CaseStatus[]
              ).map((s) => (
                <option key={s} value={s}>
                  {caseStatusLabel(s)}
                </option>
              ))}
            </select>
            <select
              className="rounded-md border border-surface-border px-2 py-1"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">Any priority</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              className="rounded-md border border-surface-border px-2 py-1"
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
            >
              <option value="updated_at">Recently updated</option>
              <option value="created_at">Newest</option>
              <option value="priority">Priority</option>
              <option value="trial_date">Trial date</option>
            </select>
            <label className="flex items-center gap-1 text-gray-600">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          </div>
        </div>

        {visible.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">
            {cases.length === 0
              ? 'No cases yet — new referrals will appear here in your Levelheaded queue.'
              : 'No cases match the current filters.'}
          </p>
        ) : (
          <ul className="divide-y divide-surface-border">
            {visible.map((c) => (
              <QueueRow
                key={c.id}
                caseItem={c}
                insight={insights[c.id]}
                searchHit={searchHits?.get(c.id)}
                isClerk={caps.isClerk}
                token={token}
                isArchived={archived.has(c.id)}
                onToggleArchive={() => toggleArchive(c.id, !archived.has(c.id))}
                onOpen={() => onOpenCase(c.id)}
                onViewDoc={(title, url, filename) => setDocViewer({ title, url, filename })}
                isAdminMediator={caps.isAdminMediator}
                onToggleFollow={caps.isClerk ? undefined : (next) => toggleFollow(c.id, next)}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Dispatch triage is an admin/admin-mediator job — hidden from regular
          mediators, who found it confusing (Danny, 2026-10-23). */}
      {(caps.role === 'admin' || caps.isAdminMediator) && (
        <div ref={unmatchedRef}>
          <UnmatchedInbox
            token={token}
            cases={cases}
            readOnly={caps.readOnly}
            onOpenCase={onOpenCase}
            onUnauthorized={onUnauthorized}
          />
        </div>
      )}

    </div>
  );
}

/** Unread internal messages, blue-dotted; active = queue sorted by most
    recent internal message (newest thread first). */
function InboxTile({ count, active, onClick }: { count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Cases with unread messages from mediators/clerks — click to sort the list by latest message"
      className={[
        'card p-3 text-left transition-colors',
        active ? 'border-brand ring-1 ring-brand' : 'hover:border-gray-300',
      ].join(' ')}
    >
      <div className="flex items-center gap-2 text-2xl font-semibold tabular-nums">
        {count}
        {count > 0 && <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-blue-500" />}
      </div>
      <div className="mt-0.5 text-xs font-medium text-gray-600">
        📬 Inbox — unread{active ? ' · sorted by latest' : ''}
      </div>
    </button>
  );
}

// Count of inbound texts/emails that couldn't be routed to a case (no
// mediator/case attached). Red when any are waiting; click jumps to the



/** Mediator-dashboard flags (spec 6.1–6.6). Data: list payload (tags,
    metadata, unpaid, internal unread) + the triage scan (booked,
    awaiting-reply). */
function caseFlags(c: CaseListItem, insight?: QueueInsight): string[] {
  const flags: string[] = [];
  const trialDays = c.trial_date ? daysUntil(c.trial_date) : null;
  if (
    OPEN_STATUSES.includes(c.status) &&
    trialDays !== null && trialDays >= 0 && trialDays <= TRIAL_SOON_DAYS &&
    insight && !insight.booked
  ) flags.push('Trial soon, no mediation');
  if (c.has_unpaid_invoice) flags.push('Party not paid');
  if (c.tags.includes('party2_unreachable')) flags.push('Party not responding');
  if (c.internal_unread) flags.push('Court question');
  // Stuck-case flags (Danny, 2026-09-17). All three only bite on OPEN
  // cases; a booked future session suppresses the quiet flag (waiting
  // for a scheduled mediation is not stuck).
  if (OPEN_STATUSES.includes(c.status)) {
    if (c.awaiting_reply) flags.push('Party waiting on a reply');
    const hasFutureSession = !!c.next_session_at && new Date(c.next_session_at).getTime() > Date.now();
    const lastTouch = c.last_comm_at ?? c.created_at;
    const quietDays = Math.floor((Date.now() - new Date(lastTouch).getTime()) / 86_400_000);
    if (!hasFutureSession && quietDays >= 5) flags.push(`Quiet ${quietDays} days`);
    const noContact = (c.parties ?? []).filter(
      (p) =>
        ['claimant', 'respondent'].includes(p.role_archetype) &&
        !p.email &&
        !p.phone &&
        // Delinquency claimants ARE the org (reached via org channels,
        // never party contact) — flagging "No contact info: Nomad" on
        // every case is pure noise.
        !(c.domain_pack_id === 'rent_delinquency' && p.role_archetype === 'claimant'),
    );
    for (const p of noContact) flags.push(`No contact info: ${p.name}`);
  }
  if ((c.metadata as { cross_court_duplicate?: { org_name?: string } }).cross_court_duplicate) {
    const dup = (c.metadata as { cross_court_duplicate?: { org_name?: string } }).cross_court_duplicate!;
    flags.push(`Possible duplicate at ${dup.org_name ?? 'another court'}`);
  }
  if (c.tags.includes('needs_triage')) flags.push('New intake');
  // Superseded by the row-level awaiting_reply flag (universal, not
  // limited to the triage-scan window) — kept only as a fallback when
  // the engine predates the field.
  if (c.awaiting_reply === undefined && (insight?.waitingOnUs ?? 0) > 0) flags.push('Unanswered party message');
  if (
    (c.metadata as { blocked_reason?: unknown }).blocked_reason ||
    c.tags.includes('off_file_review') ||
    c.tags.includes('balance_review')
  ) flags.push('Blocked');
  return flags;
}

/** Status colors (old-platform parity, Mo 2026-08-18): left stripe +
    pill. Literal class strings so Tailwind keeps them. */
/** Tile color language (Danny, 2026-09-18):
    PURPLE fully resolved · GREEN mediation scheduled/underway ·
    RED mediated without resolution · ORANGE blocked (no-show, review
    holds, returned to court) · YELLOW intake/scheduling in progress ·
    grey anything else (administrative closes). Stripe + light tile
    wash use the same hue; the label pill stays state-specific. */
function statusColors(c: CaseListItem): { stripe: string; pill: string; label: string; bg: string } {
  const md = (c.metadata ?? {}) as Record<string, unknown>;
  const label = c.clerk_status ?? caseStatusLabel(c.status);
  const P = { stripe: 'bg-purple-500', pill: 'bg-purple-100 text-purple-800', bg: 'bg-purple-50/60', label };
  const G = { stripe: 'bg-green-500', pill: 'bg-green-100 text-green-800', bg: 'bg-green-50/60', label };
  const R = { stripe: 'bg-red-500', pill: 'bg-red-100 text-red-800', bg: 'bg-red-50/60', label };
  const O = { stripe: 'bg-orange-500', pill: 'bg-orange-100 text-orange-800', bg: 'bg-orange-50/70', label };
  const Y = { stripe: 'bg-yellow-400', pill: 'bg-yellow-100 text-yellow-800', bg: 'bg-yellow-50/60', label };
  const N = { stripe: 'bg-gray-300', pill: 'bg-gray-200 text-gray-700', bg: 'bg-white', label };

  // PURPLE — fully resolved (however it closed out).
  if (
    c.status === 'RESOLVED' ||
    (c.status === 'CLOSED' && md.mediation_outcome === 'resolved') ||
    c.clerk_status === 'RESOLVED'
  ) return P;
  // RED — a mediation happened, no resolution came out of it.
  if (md.mediation_outcome === 'unresolved' || (c.clerk_status ?? '').startsWith('UNRESOLVED')) return R;
  // ORANGE — blocked: no-shows, refusals, review holds, returned to court.
  if (
    (c.clerk_status ?? '').startsWith('Blocked') ||
    md.blocked_reason ||
    md.returned_to_court === true ||
    c.tags.some((t) => ['party2_unreachable', 'no_party2_contact', 'scheduling_stall_returned', 'off_file_review', 'balance_review'].includes(t))
  ) return O;
  // GREEN — mediation booked (future session) or actively underway.
  const futureSession = !!c.next_session_at && new Date(c.next_session_at).getTime() > Date.now();
  if (futureSession || c.status === 'MEDIATING' || (c.clerk_status ?? '').startsWith('Scheduled')) return G;
  // YELLOW — intake or scheduling in progress.
  if (OPEN_STATUSES.includes(c.status)) return Y;
  return N;
}

/** Clerk-to-Levelheaded message button (spec 2.8): a quick note to the
    mediation team about this case, straight from the list. */
/** Routes to the case's Messages panel (Danny, 2026-08-28: the popup
    prompt was silly — the real thread with history lives on the case
    detail page). The sessionStorage flag tells the detail page to scroll
    the panel into view on arrival. */
function ClerkMessageButton({ caseId, onOpen }: { caseId: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="btn-secondary shrink-0 !py-1 !text-xs"
      onClick={() => {
        sessionStorage.setItem('lh-scroll-messages', caseId);
        onOpen();
      }}
    >
      Message
    </button>
  );
}

/** Row party lines: claimant (plaintiff) first, respondent (defendant)
    second; skip TBD placeholders that have no contact info yet. */
function queueParties(c: CaseListItem): NonNullable<CaseListItem['parties']> {
  const rank = (a: string) => (a === 'claimant' ? 0 : 1);
  return (c.parties ?? [])
    .filter((p) => ['claimant', 'respondent'].includes(p.role_archetype))
    .filter((p) => !(/^TBD\b/i.test(p.name) && !p.phone && !p.email))
    .sort((a, b) => rank(a.role_archetype) - rank(b.role_archetype));
}

function QueueRow({
  caseItem: c,
  insight,
  searchHit,
  isClerk,
  token,
  isArchived,
  onToggleArchive,
  onOpen,
  onViewDoc,
  isAdminMediator,
  onToggleFollow,
}: {
  caseItem: CaseListItem;
  insight?: QueueInsight;
  searchHit?: CaseSearchHit;
  isClerk: boolean;
  token: string;
  isArchived: boolean;
  onToggleArchive: () => void;
  onOpen: () => void;
  onViewDoc?: (title: string, url: string, filename: string) => void;
  /** Admin-mediator view: show an explicit intake-vs-scheduled label. */
  isAdminMediator?: boolean;
  /** Toggle following this case (undefined = control hidden, e.g. clerks). */
  onToggleFollow?: (next: boolean) => void;
}) {
  const trialDays = c.trial_date ? daysUntil(c.trial_date) : null;
  const label = caseQueueLabel(c);

  // Document indicators — every role's list row (Danny, 2026-09-08:
  // final documents must be unmissable on the list). A chip goes GREEN
  // when the document is done: stipulation/resolution fully signed, ROM
  // finalized and visible to the court. Clickable — opens the PDF.
  const docs = c.documents;
  const resolveDocPdf = async (type: ClerkDocType) => {
    const res = await listCaseDocuments(token, c.id);
    const doc = res.documents.find((d) => d.doc_type === type);
    if (!doc) return null;
    const url = await getDocumentPdfUrl(token, doc.id);
    const meta = CLERK_DOC_META[type];
    return { url, title: meta.title, filename: `${label.caseNo ?? 'case'}-${meta.slug}.pdf` };
  };
  // Preview = the actual PDF in the popup (Danny, 2026-08-28 — the HTML
  // view didn't match what the court receives).
  const openDoc = (type: ClerkDocType) => (e: ReactMouseEvent) => {
    e.stopPropagation();
    void (async () => {
      try {
        const pdf = await resolveDocPdf(type);
        if (!pdf) return;
        if (onViewDoc) onViewDoc(`${pdf.title} — ${label.caseNo ?? ''}`.trim(), pdf.url, pdf.filename);
      } catch {
        // Fall back to the tab-open path (popup-blocker aware).
        void openClerkListDocument(token, c.id, type);
      }
    })();
  };
  const downloadDoc = (type: ClerkDocType) => (e: ReactMouseEvent) => {
    e.stopPropagation();
    void (async () => {
      try {
        const pdf = await resolveDocPdf(type);
        if (!pdf) return;
        const a = document.createElement('a');
        a.href = pdf.url;
        a.download = pdf.filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(pdf.url), 60_000);
      } catch {
        void openClerkListDocument(token, c.id, type);
      }
    })();
  };
  const docChips: Array<{ type: ClerkDocType; label: string; status: string; final: boolean }> = [
    ...(docs?.rom ? [{ type: 'rom' as const, label: 'RoM', status: docs.rom, final: docs.rom_court_shared === true }] : []),
    ...(docs?.stipulation ? [{ type: 'stipulation' as const, label: 'Stipulation', status: docs.stipulation, final: docs.stipulation === 'signed' }] : []),
    ...(docs?.resolution_agreement ? [{ type: 'resolution_agreement' as const, label: 'Resolution', status: docs.resolution_agreement, final: docs.resolution_agreement === 'signed' }] : []),
    ...(docs?.certificate ? [{ type: 'certificate_of_attempts' as const, label: 'COA', status: docs.certificate, final: false }] : []),
  ];
  const chipTone = (final: boolean) =>
    final ? 'bg-green-100 text-green-800 font-medium' : 'bg-surface-muted text-brand';

  // ── Clerk list row (spec 2.1–2.8): case number most prominent, party
  // contact checkmarks, canonical status, doc indicators (clickable —
  // they open the document itself), message + archive buttons.
  if (isClerk) {
    const colors = statusColors(c);
    return (
      <li className={`relative flex items-start gap-3 ${colors.bg} py-3 pl-4 pr-3 hover:bg-surface-muted`}>
        <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${colors.stripe}`} />
        <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpen}>
          <div className="flex flex-wrap items-baseline gap-2">
            {label.caseNo && <span className="text-base font-bold">{label.caseNo}</span>}
            <span className="text-sm font-semibold">{label.name}</span>
            {isArchived && (
              <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">Archived</span>
            )}
            {c.trial_date && (
              <span className={`text-xs ${trialDays !== null && trialDays <= 7 ? 'font-medium text-red-600' : 'text-gray-500'}`}>
                — Trial {formatDate(c.trial_date)}
              </span>
            )}
            {c.has_messages && <span title="Messages exist on this case">✉</span>}
            {c.internal_unread && (
              <span className="rounded-full bg-brand-subtle px-1.5 py-0.5 text-[10px] font-medium text-brand" title="Unread internal message">
                ● internal
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
            {(c.parties ?? [])
              .filter((p) => ['claimant', 'respondent'].includes(p.role_archetype))
              .map((p) => (
                <span key={p.id ?? p.name} className={p.contacted ? 'text-green-700' : 'text-red-600'} title={p.contacted ? 'Contacted' : 'Not yet contacted'}>
                  {p.contacted ? '✓' : '✗'} {p.name}
                </span>
              ))}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {c.clerk_status && (
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${colors.pill}`}>{c.clerk_status}</span>
            )}
            {isAdminMediator && (
              c.next_session_at
                ? <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-800" title="Scheduled — now a mediator's case">Scheduled{c.mediator_name ? ` · ${c.mediator_name}` : ''}</span>
                : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800" title="Pre-mediation — not yet scheduled">Intake · not scheduled</span>
            )}
            {c.next_session_at && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800" title="Mediation session booked">
                📅 {formatDateTime(c.next_session_at)}
              </span>
            )}
            {docs && docs.additional > 0 && (
              <span className="rounded-full bg-surface-muted px-2 py-0.5 text-[11px] text-gray-700">+{docs.additional} doc{docs.additional === 1 ? '' : 's'}</span>
            )}
          </div>
        </button>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {onToggleFollow && (
            <button
              type="button"
              title={c.followed ? 'Unfollow this case' : 'Follow this case'}
              className={`text-base leading-none ${c.followed ? 'text-amber-500' : 'text-gray-300 hover:text-amber-500'}`}
              onClick={(e) => { e.stopPropagation(); onToggleFollow(!c.followed); }}
            >
              {c.followed ? '★' : '☆'}
            </button>
          )}
          {/* Stip + RoM file chips on clerk tiles ONLY when the document
              exists (Danny, 2026-09-18 — no grey placeholders). The
              stipulation redaction holds: before full execution it does
              not exist for clerks. */}
          {(
            [
              { type: 'stipulation' as const, label: 'Stipulation', status: docs?.stipulation ?? null, final: docs?.stipulation === 'signed' },
              { type: 'rom' as const, label: 'RoM', status: docs?.rom ?? null, final: docs?.rom_court_shared === true },
            ]
          )
            .filter((chip) => chip.status)
            .map((chip) => (
              <span key={chip.type} className="flex items-center gap-1">
                <span
                  role="button"
                  tabIndex={0}
                  title={`Preview the ${CLERK_DOC_META[chip.type].title} PDF`}
                  className={`cursor-pointer rounded-full px-2 py-0.5 text-[11px] underline-offset-2 hover:underline ${chipTone(chip.final)}`}
                  onClick={openDoc(chip.type)}
                >
                  {chip.final ? '✓' : '👁'} 📄 {chip.label} · {chip.status}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  title={`Download the ${CLERK_DOC_META[chip.type].title} PDF`}
                  className="cursor-pointer rounded-full bg-surface-muted px-1.5 py-0.5 text-[11px] text-brand hover:underline"
                  onClick={downloadDoc(chip.type)}
                >
                  ⬇
                </span>
              </span>
            ))}
          {docChips
            .filter((chip) => chip.type !== 'stipulation' && chip.type !== 'rom')
            .map((chip) => (
            <span key={chip.type} className="flex items-center gap-1">
              <span
                role="button"
                tabIndex={0}
                title={`Preview the ${CLERK_DOC_META[chip.type].title} PDF`}
                className={`cursor-pointer rounded-full px-2 py-0.5 text-[11px] underline-offset-2 hover:underline ${chipTone(chip.final)}`}
                onClick={openDoc(chip.type)}
              >
                {chip.final ? '✓' : '👁'} {chip.label} · {chip.status}
              </span>
              <span
                role="button"
                tabIndex={0}
                title={`Download the ${CLERK_DOC_META[chip.type].title} PDF`}
                className="cursor-pointer rounded-full bg-surface-muted px-1.5 py-0.5 text-[11px] text-brand hover:underline"
                onClick={downloadDoc(chip.type)}
              >
                ⬇
              </span>
            </span>
          ))}
          <ClerkMessageButton caseId={c.id} onOpen={onOpen} />
          <ArchiveButton isArchived={isArchived} onToggle={onToggleArchive} />
        </div>
      </li>
    );
  }

  const colors = statusColors(c);
  const flags = caseFlags(c, insight);
  const flagged = flags.length > 0;
  const fresh = isNewCase(c);
  // Near-trial urgency (independent of flags): red ≤7d, amber ≤21d.
  const trialUrgency: 'red' | 'amber' | null =
    trialDays !== null && trialDays >= 0 && OPEN_STATUSES.includes(c.status)
      ? trialDays <= 7 ? 'red' : trialDays <= TRIAL_SOON_DAYS ? 'amber' : null
      : null;
  // Tile wash = the state color (Danny, 2026-09-18). Urgency and flags
  // stay visible through their chips; the near-trial red tint only
  // overrides states that are still in progress (yellow/green tiles).
  const rowBg =
    (flagged || trialUrgency === 'red') && OPEN_STATUSES.includes(c.status)
      ? 'bg-red-50/60'
      : colors.bg;
  return (
    <li className={`relative flex items-center gap-3 ${rowBg} py-2.5 pl-4 pr-3 hover:bg-surface-muted`}>
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${colors.stripe}`} />
      <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpen}>
        <div className="flex items-center gap-2">
          {/* Source leads the row; internal case ids stay out of the UI
              (still searchable). Old-system parity, Danny 2026-08-18. */}
          <span className="shrink-0 rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
            {label.partner ?? 'Levelheaded'}
          </span>
          <span className="truncate text-sm font-medium">
            {label.caseNo ? `${label.caseNo} · ${label.name}` : label.name}
          </span>
          {fresh && (
            <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800" title="New case — arrived recently or flagged for triage">
              New
            </span>
          )}
          {c.internal_unread && (
            <span className="shrink-0 rounded-full bg-brand-subtle px-1.5 py-0.5 text-[10px] font-medium text-brand" title="Unread internal message">
              ● internal
            </span>
          )}
          {isArchived && (
            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
              Archived
            </span>
          )}
          {!c.assigned_mediator_id && (
            <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800" title="No mediator assigned yet — needs dispatch">
              Unassigned
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <StageMiniBar stage={deriveCaseStage(c.status, { mediated: insight?.booked })} />
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${colors.pill}`}>{colors.label}</span>
          <PriorityBadge priority={c.priority} />
          {c.next_session_at && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800" title="Mediation session booked">
              📅 {formatDateTime(c.next_session_at)}
            </span>
          )}
          {docChips.map((chip) => (
            <span
              key={chip.type}
              role="button"
              tabIndex={0}
              title={chip.final ? `${CLERK_DOC_META[chip.type].title} — complete. Click to preview the PDF.` : `${CLERK_DOC_META[chip.type].title} — ${chip.status}. Click to preview the PDF.`}
              className={`cursor-pointer rounded-full px-2 py-0.5 text-[11px] underline-offset-2 hover:underline ${chipTone(chip.final)}`}
              onClick={openDoc(chip.type)}
            >
              {chip.final ? '✓' : '📄'} {chip.label}{chip.final ? '' : ` · ${chip.status}`}
            </span>
          ))}
          {caseFlags(c, insight).map((f) => (
            <span key={f} className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
              ⚑ {f}
            </span>
          ))}
          {c.trial_date && (
            trialUrgency ? (
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${trialUrgency === 'red' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}
                title={`Trial ${formatDate(c.trial_date)}`}
              >
                ⏰ Trial {trialDays === 0 ? 'today' : `in ${trialDays}d`}
              </span>
            ) : (
              <span>Trial {formatDate(c.trial_date)}</span>
            )
          )}
          {insight && insight.waitingOnUs > 0 && (
            <span className="text-amber-700">↩ {insight.waitingOnUs} awaiting reply</span>
          )}
          {insight && insight.schedulingWaitingOn.length > 0 && (
            <span className="text-blue-700">
              ⏳ waiting on {insight.schedulingWaitingOn.join(', ')}
            </span>
          )}
          {insight && insight.pendingDrafts > 0 && (
            <span className="text-purple-700">✉ {insight.pendingDrafts} draft(s)</span>
          )}
          {c.tags.includes('party2_unreachable') && (
            <span className="font-medium text-red-600" title="Party 2 did not respond to 3 automated invitations over 3 days — reach out personally (call works best)">
              ☎ personal outreach needed
            </span>
          )}
        </div>
        {(c.mediator_name || queueParties(c).length > 0) && (
          <div className="mt-1 space-y-0.5 text-xs text-gray-600">
            {c.mediator_name && (
              <div>
                <span className="font-medium text-gray-500">Mediator:</span> {c.mediator_name}
              </div>
            )}
            {queueParties(c).map((p) => (
              <div key={p.name} className="truncate">
                <span className="font-medium text-gray-500">{partyRoleLabel(p, c.org_source_type)}:</span>{' '}
                {p.name}
                {(p.phone || p.email) && (
                  <span className="text-gray-400">
                    {' '}— {[p.phone, p.email].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {searchHit?.snippet && (
          <div className="mt-0.5 truncate text-xs text-gray-500">
            <span className="font-medium text-gray-400">
              {searchHit.matched.includes('message') ? 'in messages: ' : 'matched: '}
            </span>
            &ldquo;{searchHit.snippet}&rdquo;
          </div>
        )}
      </button>
      <ArchiveButton isArchived={isArchived} onToggle={onToggleArchive} />
    </li>
  );
}

/** List-view document open (clerk spec follow-up, Danny 2026-08-22):
    resolve the case's latest doc of the type, then PDF-first blob open
    with the FilesPanel popup-blocker fallback. */
type ClerkDocType = 'rom' | 'stipulation' | 'resolution_agreement' | 'certificate_of_attempts';
const CLERK_DOC_META: Record<ClerkDocType, { title: string; slug: string }> = {
  rom: { title: 'Report of Mediation', slug: 'report-of-mediation' },
  stipulation: { title: 'Stipulation', slug: 'stipulation' },
  resolution_agreement: { title: 'Resolution Agreement', slug: 'resolution-agreement' },
  certificate_of_attempts: { title: 'Certificate of Attempts', slug: 'certificate-of-attempts' },
};

async function openClerkListDocument(
  token: string,
  caseId: string,
  type: ClerkDocType,
): Promise<void> {
  try {
    const res = await listCaseDocuments(token, caseId);
    const doc = res.documents.find((d) => d.doc_type === type);
    if (!doc) return;
    let url: string;
    try {
      url = await getDocumentPdfUrl(token, doc.id);
    } catch {
      const html = await getDocumentHtml(token, doc.id);
      url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    }
    const win = window.open(url, '_blank');
    if (!win) {
      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    console.warn('Could not open the document:', err);
  }
}

function PriorityBadge({ priority }: { priority: string }) {
  const tone =
    priority === 'critical'
      ? 'bg-red-100 text-red-800'
      : priority === 'high'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-gray-100 text-gray-600';
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{priority}</span>
  );
}

/** Cohort outreach launch: one audited approval starts the ladder for a
    whole imported cohort — preview (dry run) first, then launch. Sends
    are paced by the engine (~10 per 15 min, contact hours only). */
function OutreachLaunchPanel({ token }: { token: string }) {
  const [cohorts, setCohorts] = useState<Array<{ cohort: string; count: number }>>([]);
  const [cohort, setCohort] = useState('');
  const [preview, setPreview] = useState<{ eligible: OutreachEligible[]; blocked: OutreachBlocked[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getOutreachCohorts(token)
      .then((res) => setCohorts(res.cohorts))
      .catch(() => setCohorts([]));
  }, [token]);

  if (cohorts.length === 0) return null;

  const runPreview = async () => {
    if (!cohort) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setConfirming(false);
    try {
      const res = await launchOutreach(token, { cohort, dry_run: true });
      setPreview({ eligible: res.eligible, blocked: res.blocked });
    } catch (err) {
      setError(err instanceof EngineError ? err.message : 'Preview failed.');
    } finally {
      setBusy(false);
    }
  };

  const runLaunch = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await launchOutreach(token, { cohort, dry_run: false });
      setResult(
        `Approved outreach for ${res.approved} tenant(s). Messages go out about 10 per 15 minutes during contact hours (9am-8pm Mon-Fri). The ladder continues automatically until each tenant engages.`,
      );
      setPreview(null);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof EngineError ? err.message : 'Launch failed.');
    } finally {
      setBusy(false);
    }
  };

  const reasonLabel: Record<string, string> = {
    no_magic_token: 'Not activated (no portal token)',
    no_contact: 'No phone or email on file',
    already_started: 'Outreach already started',
    already_approved: 'Already approved',
    not_in_outreach_state: 'Not in an outreach state',
  };

  return (
    <section className="card p-4">
      <div className="section-label">Launch outreach</div>
      <p className="mt-1 text-xs text-gray-500">
        Start the portal-link ladder for an imported cohort in one approval. Preview shows exactly
        who gets messaged and who is blocked.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          className="input !w-auto text-sm"
          value={cohort}
          onChange={(e) => {
            setCohort(e.target.value);
            setPreview(null);
            setResult(null);
          }}
        >
          <option value="">Select a cohort…</option>
          {cohorts.map((c) => (
            <option key={c.cohort} value={c.cohort}>
              {c.cohort} ({c.count} open cases)
            </option>
          ))}
        </select>
        <button type="button" className="btn-secondary !py-1 !text-xs" disabled={!cohort || busy} onClick={() => void runPreview()}>
          {busy && !preview ? 'Checking…' : 'Preview'}
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
      {result && <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-xs text-green-900">{result}</div>}
      {preview && (
        <div className="mt-3 space-y-3 text-sm">
          <div>
            <div className="text-xs font-semibold text-gray-700">
              Will be messaged ({preview.eligible.length})
            </div>
            <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto text-xs">
              {preview.eligible.map((e) => (
                <li key={e.case_id} className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">
                    {e.tenant_name}
                    {e.property ? <span className="text-gray-400"> · {e.property}</span> : null}
                  </span>
                  <span className="shrink-0 text-gray-500">
                    ${e.balance.toLocaleString()} · {e.channels.join('+') || 'no channel'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {preview.blocked.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-amber-700">Blocked ({preview.blocked.length})</div>
              <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto text-xs text-amber-800">
                {preview.blocked.map((b) => (
                  <li key={b.case_id}>
                    {b.tenant_name ?? b.case_id.slice(0, 8)} — {reasonLabel[b.reason] ?? b.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {preview.eligible.length > 0 && (
            confirming ? (
              <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                <span>Message {preview.eligible.length} tenant(s), starting within 15 minutes?</span>
                <button type="button" className="font-semibold hover:underline" disabled={busy} onClick={() => void runLaunch()}>
                  {busy ? 'Launching…' : 'Yes, launch'}
                </button>
                <button type="button" className="hover:underline" onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" className="btn-primary !py-1.5 !text-sm" onClick={() => setConfirming(true)}>
                Launch outreach for {preview.eligible.length} tenant(s)
              </button>
            )
          )}
        </div>
      )}
    </section>
  );
}


/* ════════ Practice onboarding checklist (Mediator OS Phase 2) ════════ */

function PracticeOnboardingCard({ practice }: { practice: Awaited<ReturnType<typeof getMyPractice>>['practice'] }) {
  if (!practice) return null;
  const c = practice.checklist;
  const items: Array<{ done: boolean; label: string; hint: string }> = [
    { done: c.zoom_connected, label: 'Connect your Zoom', hint: 'Availability tab → Your Zoom account' },
    { done: c.availability_set, label: 'Set your weekly availability', hint: 'Availability tab' },
    { done: !!c.intake_link, label: 'Your intake link', hint: c.intake_link ? `share ${c.intake_link}` : 'ask Levelheaded to set your slug' },
    { done: c.first_case_created, label: 'Run a test case', hint: 'file through your intake link and walk it end to end' },
  ];
  const remaining = items.filter((i) => !i.done).length;
  if (remaining === 0) return null;
  return (
    <section className="card border-brand/30 p-4">
      <div className="flex items-center gap-2">
        <div className="section-label">Getting started — {practice.name}</div>
        <span className="rounded-full bg-brand-subtle px-2 py-0.5 text-[11px] font-medium text-brand">{items.length - remaining} of {items.length} done</span>
      </div>
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((i) => (
          <li key={i.label} className="flex items-baseline gap-2">
            <span className={i.done ? 'text-teal-600' : 'text-gray-300'}>{i.done ? '✓' : '○'}</span>
            <span className={i.done ? 'text-gray-400 line-through' : 'text-gray-800'}>{i.label}</span>
            {!i.done && <span className="text-xs text-gray-400">— {i.hint}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}
